import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Moon, Sun, type LucideIcon } from 'lucide-react';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { establecerTema, escucharSistema, sistemaEnOscuro, temaEfectivo } from '@/lib/tema';

/** true si el iPhone está en modo oscuro; se actualiza al cambiarlo. */
function useSistemaOscuro(): boolean {
  const [oscuro, setOscuro] = useState(sistemaEnOscuro);
  useEffect(() => escucharSistema(setOscuro), []);
  return oscuro;
}

const OPCIONES: ReadonlyArray<{ tema: 'claro' | 'oscuro'; nombre: string; icono: LucideIcon }> = [
  { tema: 'claro', nombre: 'Modo claro', icono: Sun },
  { tema: 'oscuro', nombre: 'Modo oscuro', icono: Moon },
];

/**
 * Sol y luna del encabezado (diseño Foco): sol elige el tema claro y luna el
 * oscuro. El botón activo muestra el tema que se ve, también cuando la
 * preferencia es "según el sistema", que sigue disponible en Ajustes.
 *
 * Guarda la elección en los ajustes, igual que Ajustes (Req. 8.3), y la aplica
 * al momento: los íconos vienen dentro de la app y funcionan sin conexión.
 */
export function SelectorTema() {
  const tema = useLiveQuery(async () => (await leerSettings()).tema, []);
  const sistemaOscuro = useSistemaOscuro();
  const visible = temaEfectivo(tema ?? 'sistema', sistemaOscuro);

  async function elegir(nuevo: 'claro' | 'oscuro') {
    // Primero se ve; si guardar fallara (almacenamiento lleno), el tema vuelve
    // a lo guardado al reabrir la app, sin perder ningún dato.
    establecerTema(nuevo);
    try {
      await actualizarSettings({ tema: nuevo });
    } catch {
      // Ajustes muestra el error si el usuario lo intenta desde ahí.
    }
  }

  return (
    <div role="group" aria-label="Apariencia" className="flex items-center rounded-[30px] bg-superficie-suave p-[3px]">
      {OPCIONES.map(({ tema: opcion, nombre, icono: Icono }) => {
        const activo = visible === opcion;
        return (
          <button
            key={opcion}
            type="button"
            aria-label={nombre}
            aria-pressed={activo}
            title={nombre}
            onClick={() => void elegir(opcion)}
            className={[
              'grid size-11 place-items-center rounded-[25px] transition-colors',
              activo
                ? 'bg-superficie text-texto shadow-[0_1px_3px_var(--sombra)]'
                : 'text-texto-suave',
            ].join(' ')}
          >
            <Icono className="size-[18px]" />
          </button>
        );
      })}
    </div>
  );
}
